// SpaceContentListPage.jsx (React 19 + MUI, Toss-style UI)
// - AND(연계) 필터 적용 + 키워드/테마 텍스트 입력 제거
// - 테마 + 키워드 "전체 새로고침" 버튼 추가(필터 옵션만 리프레시)

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../../../util/api";
import { keyVisualApi } from "../api/keyVisualApi";

import SpaceContentDetailModal from "./modal/SpaceContentDetailModal";
import SpaceContentEditModal from "./modal/SpaceContentEditModal";
import { confirm as confirmDialog } from "../../../../util/confirmDialog";
import { safeImgUrl } from "../../../../util/imageUrl";

// MUI
import {
    Box,
    Button,
    Chip,
    Container,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Paper,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { notify } from "../../../../util/toast";
import { runApiAction } from "../../../../util/apiFeedback";
import LoadingDots from "../../../../component/common/LoadingDots";
import ActionButton from "../../../../component/common/ActionButton";
import ComboBox from "../../../../component/common/ComboBox";

import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import HomeWorkRoundedIcon from "@mui/icons-material/HomeWorkRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { ds } from "../../../../styles/designSystem";

const PAGE_SIZE = 30;

export function SpaceContentListPage() {
    const API_BASE = process.env.REACT_APP_API_BASE_URL;
    const API_REAL = process.env.REACT_APP_API_REAL_URL;

    const [data, setData] = useState([]);
    const [filtered, setFiltered] = useState([]);

    const [locationTypeFilter, setLocationTypeFilter] = useState(""); // "", "실내", "실외"
    const [activeThemes, setActiveThemes] = useState([]);
    const [activeKeywords, setActiveKeywords] = useState([]);
    const [eventNameFilter, setEventNameFilter] = useState("");
    const [locationFilter, setLocationFilter] = useState("");

    const [allThemes, setAllThemes] = useState([]);
    const [allKeywords, setAllKeywords] = useState([]);
    const [allEventNames, setAllEventNames] = useState([]);
    const [allLocations, setAllLocations] = useState([]);

    const [detailView, setDetailView] = useState(null);
    const [editingContent, setEditingContent] = useState(null);

    const [loading, setLoading] = useState(false);

    // 무한 스크롤 페이지네이션 상태
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const sentinelRef = useRef(null);

    // "필터 옵션 새로고침" 로딩(키워드+테마)
    const [filtersRefreshing, setFiltersRefreshing] = useState(false);

    // normalize: 앞/뒤 공백 제거 + "모든 공백 제거"
    const normalizeKeyword = useCallback(
        (s) => String(s ?? "").trim().replace(/\s+/g, ""),
        []
    );

    /**
     * 페이지 단위 콘텐츠 로드 — 백엔드가 5개 필터 처리 후 페이지네이션.
     * filterArgs 가 변경되면 첫 페이지부터 재로드.
     *
     * <p>필터 chip 옵션(테마/키워드)은 별도 엔드포인트(/themes, /keywords)로 전체 옵션 로드하므로
     * 여기서는 콘텐츠만 가져오고 옵션은 건드리지 않음.
     */
    const loadContentsPage = useCallback(async (pageNum, append, filterArgs) => {
        try {
            setLoading(true);
            const data = await keyVisualApi.space.getListPaged({
                page: pageNum,
                size: PAGE_SIZE,
                ...filterArgs,
            });
            const items = Array.isArray(data?.content) ? data.content : [];
            setData((prev) => append ? [...prev, ...items] : items);
            setFiltered((prev) => append ? [...prev, ...items] : items);
            setHasMore(pageNum + 1 < (data?.totalPages ?? 0));
            setPage(pageNum);
        } catch (e) {
            console.error(e);
            notify("목록을 불러오지 못했습니다.", "error");
            // 에러 시 무한 스크롤 중단 — 안 그러면 sentinel 이 계속 보여 useEffect 가 재호출 루프 탐.
            setHasMore(false);
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * 필터 chip 옵션(전체 테마·키워드)을 백엔드 전용 엔드포인트로 1회 로드.
     * 필터 결과와 무관하게 항상 모든 옵션이 chip 으로 노출되도록 한다.
     */
    const loadFilterOptions = useCallback(async () => {
        try {
            const [themesData, keywordsData] = await Promise.all([
                api.get(`${API_BASE}/space-content/themes`),
                api.get(`${API_BASE}/space-content/keywords`),
            ]);
            const rawThemes = Array.isArray(themesData) ? themesData : (themesData?.data || []);
            const rawKeywords = Array.isArray(keywordsData) ? keywordsData : (keywordsData?.data || []);

            const parsedThemes = rawThemes
                .flatMap((t) => String(t).split(","))
                .map((s) => normalizeKeyword(s))
                .filter(Boolean);
            const parsedKeywords = rawKeywords
                .flatMap((k) => String(k).split(","))
                .map((s) => normalizeKeyword(s))
                .filter(Boolean);

            setAllThemes(Array.from(new Set(parsedThemes)).sort((a, b) => a.localeCompare(b, "ko")));
            setAllKeywords(Array.from(new Set(parsedKeywords)).sort((a, b) => a.localeCompare(b, "ko")));
        } catch (e) {
            console.error(e);
        }
    }, [API_BASE, normalizeKeyword]);

    // 현재 필터 상태를 백엔드 호출 파라미터로 변환
    const currentFilterArgs = useMemo(() => ({
        locationType: locationTypeFilter || undefined,
        themes: activeThemes.length > 0 ? activeThemes : undefined,
        keywords: activeKeywords.length > 0 ? activeKeywords : undefined,
        eventName: eventNameFilter || undefined,
        location: locationFilter || undefined,
    }), [locationTypeFilter, activeThemes, activeKeywords, eventNameFilter, locationFilter]);

    // 호환성 유지 — 기존 fetchList 호출처 영향 없도록.
    const fetchList = useCallback(() => loadContentsPage(0, false, currentFilterArgs),
            [loadContentsPage, currentFilterArgs]);

    // 마운트 시 chip 필터 옵션 1회 로드 (전체 옵션, 필터와 무관)
    useEffect(() => {
        loadFilterOptions();
    }, [loadFilterOptions]);

    // 마운트 + 필터 변경 시 첫 페이지 재로드 (백엔드 필터 적용)
    useEffect(() => {
        loadContentsPage(0, false, currentFilterArgs);
    }, [loadContentsPage, currentFilterArgs]);

    // 무한 스크롤 — 백엔드가 필터 처리하므로 항상 동작
    useEffect(() => {
        const node = sentinelRef.current;
        if (!node || !hasMore || loading) return;
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                loadContentsPage(page + 1, true, currentFilterArgs);
            }
        }, { threshold: 0.1, rootMargin: "200px" });
        observer.observe(node);
        return () => observer.disconnect();
    }, [hasMore, loading, page, loadContentsPage, currentFilterArgs]);

    /**
     * 전체 새로고침(필터 옵션용)
     * - themes, keywords 둘 다 다시 호출해서 allThemes/allKeywords 갱신
     * - normalizeKeyword 기준으로 공백 제거 + 중복 제거
     */
    const refreshThemeAndKeywordOptions = useCallback(async () => {
        try {
            setFiltersRefreshing(true);

            const [themesData, keywordsData] = await Promise.all([
                api.get(`${API_BASE}/space-content/themes`),
                api.get(`${API_BASE}/space-content/keywords`),
            ]);

            const rawThemes = Array.isArray(themesData) ? themesData : (themesData?.data || []);
            const rawKeywords = Array.isArray(keywordsData) ? keywordsData : (keywordsData?.data || []);

            const parsedThemes = rawThemes
                .flatMap((t) => String(t).split(","))
                .map((s) => normalizeKeyword(s))
                .filter(Boolean);

            const parsedKeywords = rawKeywords
                .flatMap((k) => String(k).split(","))
                .map((s) => normalizeKeyword(s))
                .filter(Boolean);

            const uniqueThemes = Array.from(new Set(parsedThemes)).sort((a, b) =>
                a.localeCompare(b, "ko")
            );
            const uniqueKeywords = Array.from(new Set(parsedKeywords)).sort((a, b) =>
                a.localeCompare(b, "ko")
            );

            setAllThemes(uniqueThemes);
            setAllKeywords(uniqueKeywords);

            notify("테마/키워드를 새로고침했습니다.", "success");
        } catch (e) {
            console.error(e);
            notify("새로고침 실패", "error");
        } finally {
            setFiltersRefreshing(false);
        }
    }, [API_BASE, normalizeKeyword]);

    // toggle (normalizeKeyword 기준)
    const toggleNormalized = useCallback(
        (value, list, setList) => {
            const v = normalizeKeyword(value);
            if (!v) return;

            setList((prev) => {
                const cur = (prev || []).map(normalizeKeyword).filter(Boolean);
                const set = new Set(cur);
                if (set.has(v)) set.delete(v);
                else set.add(v);
                return Array.from(set);
            });
        },
        [normalizeKeyword]
    );

    const resetFilters = useCallback(() => {
        setLocationTypeFilter("");
        setActiveThemes([]);
        setActiveKeywords([]);
        setEventNameFilter("");
        setLocationFilter("");
        notify("필터가 초기화되었습니다.", "success");
    }, []);

    // Chip 선택 상태 판정용 — normalizeKeyword 기준의 Set.
    const activeThemeSet = useMemo(
        () => new Set((activeThemes || []).map(normalizeKeyword).filter(Boolean)),
        [activeThemes, normalizeKeyword]
    );
    const activeKeywordSet = useMemo(
        () => new Set((activeKeywords || []).map(normalizeKeyword).filter(Boolean)),
        [activeKeywords, normalizeKeyword]
    );

    // 백엔드가 5개 필터를 모두 처리하므로 클라이언트 측 필터링 제거.
    // data 가 곧 필터링된 결과 → filtered 는 data 와 동기화만.
    useEffect(() => {
        setFiltered(data);
    }, [data]);

    const handleDelete = useCallback(
        async (id) => {
            if (!(await confirmDialog({ message: "정말 삭제하시겠습니까?" }))) return;
            return runApiAction({
                request: () => api.delete(`${API_BASE}/space-content/${id}`),
                successMsg: "삭제 완료",
                errorMsg: "삭제 실패",
                setLoading,
                onSuccess: () => setData((prev) => (prev || []).filter((d) => d.id !== id)),
            });
        },
        [API_BASE]
    );

    const handleUpdate = useCallback((updatedItem) => {
        setData((prev) =>
            (prev || []).map((d) => (d.id === updatedItem.id ? updatedItem : d))
        );
        setEditingContent(null);
    }, []);

    // 카드 단위 "목록 로딩" busy 키 — 다운로드 버튼 → API 호출 후 모달 open 까지의 짧은 구간.
    // 실제 파일 다운로드는 모달 안에서 사용자가 항목별로 트리거.
    const [downloadingKey, setDownloadingKey] = useState(null);
    const downloadKeyOf = (item) => `${item.eventName}::${item.location}`;

    // 다운로드 목록 모달 state — null 이면 닫힘.
    //   meta: { event, location, safeBase, files: [{filename, url}] }
    //   doneIndexes: Set<number> 다운로드 완료된 row 인덱스 (체크 표시용)
    //   busyIndex: number | null — 현재 fetch 중인 row 인덱스 (개별 스피너용)
    const [downloadModal, setDownloadModal] = useState(null);
    const [downloadDone, setDownloadDone] = useState(new Set());
    const [downloadBusyIndex, setDownloadBusyIndex] = useState(null);
    // 전체 다운로드 진행 중 플래그. true 면 모든 개별 버튼 비활성 + 진행률 표시.
    const [downloadingAll, setDownloadingAll] = useState(false);

    const handleDownload = useCallback(
        async (item) => {
            const key = downloadKeyOf(item);
            setDownloadingKey(key);
            try {
                const files = await api.get(`${API_BASE}/space-content/file-urls`, {
                    event: item.eventName,
                    location: item.location,
                });
                if (!Array.isArray(files) || files.length === 0) {
                    notify("다운로드할 파일이 없습니다.", "warning");
                    return;
                }
                const safeBase = `${item.eventName}_${item.location}`.replace(/[\\/:*?"<>|]/g, "_");
                // 모달 open — 자동 다운로드는 하지 않음. 사용자가 항목별 버튼으로 직접.
                setDownloadModal({
                    event: item.eventName,
                    location: item.location,
                    safeBase,
                    files,
                });
                setDownloadDone(new Set());
                setDownloadBusyIndex(null);
            } catch (e) {
                console.error(e);
                notify(e?.message || "다운로드 목록 로딩 실패", "error");
            } finally {
                setDownloadingKey(null);
            }
        },
        [API_BASE]
    );

    // 모달 안에서 호출 — 단일 파일 fetch + blob + 강제 다운로드.
    //   cross-origin <a download> 는 무시되므로 fetch→blob→same-origin blob URL 패턴 유지.
    const handleDownloadOne = useCallback(async (index) => {
        if (!downloadModal) return;
        const f = downloadModal.files[index];
        if (!f) return;
        setDownloadBusyIndex(index);
        try {
            const res = await fetch(f.url);
            if (!res.ok) {
                notify(`다운로드 실패 — HTTP ${res.status}`, "error");
                return;
            }
            const blob = await res.blob();
            const ext = (f.filename || "").split(".").pop() || "bin";
            const downloadName = `${downloadModal.safeBase}_${index + 1}.${ext}`;
            const objectUrl = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = objectUrl;
            link.download = downloadName;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(objectUrl);
            setDownloadDone((prev) => {
                const next = new Set(prev);
                next.add(index);
                return next;
            });
        } catch (err) {
            console.warn("[3D 다운로드] 예외", err);
            notify("다운로드 실패 — 잠시 후 다시 시도해주세요", "error");
        } finally {
            setDownloadBusyIndex(null);
        }
    }, [downloadModal]);

    // 전체 다운로드 — 미완료 항목만 순차 처리. 이미 받은 항목은 skip.
    // 브라우저 "여러 파일 자동 저장" 차단 회피를 위해 250ms 간격 + 사용자 클릭 컨텍스트 안에서 시작.
    const handleDownloadAll = useCallback(async () => {
        if (!downloadModal) return;
        setDownloadingAll(true);
        try {
            const files = downloadModal.files;
            let okCount = 0;
            for (let i = 0; i < files.length; i++) {
                if (downloadDone.has(i)) {
                    okCount++;
                    continue;
                }
                const f = files[i];
                setDownloadBusyIndex(i);
                try {
                    const res = await fetch(f.url);
                    if (!res.ok) {
                        console.warn(`[3D 전체 다운로드] ${i + 1}/${files.length} HTTP ${res.status}`);
                        continue;
                    }
                    const blob = await res.blob();
                    const ext = (f.filename || "").split(".").pop() || "bin";
                    const downloadName = `${downloadModal.safeBase}_${i + 1}.${ext}`;
                    const objectUrl = URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = objectUrl;
                    link.download = downloadName;
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    URL.revokeObjectURL(objectUrl);
                    setDownloadDone((prev) => {
                        const next = new Set(prev);
                        next.add(i);
                        return next;
                    });
                    okCount++;
                    await new Promise((r) => setTimeout(r, 250));
                } catch (err) {
                    console.warn(`[3D 전체 다운로드] ${i + 1}/${files.length} 예외`, err);
                }
            }
            if (okCount === files.length) {
                notify(`전체 ${files.length}개 다운로드 완료`, "success");
            } else {
                notify(`${okCount}/${files.length}개 다운로드 — 실패 항목은 개별 다시 시도하세요`, "warning");
            }
        } finally {
            setDownloadBusyIndex(null);
            setDownloadingAll(false);
        }
    }, [downloadModal, downloadDone]);

    // Toss 느낌: 활성 필터 칩들
    const activeFilterChips = useMemo(() => {
        const chips = [];

        if (locationTypeFilter) {
            chips.push({
                key: `lt:${locationTypeFilter}`,
                label: `실내/실외: ${locationTypeFilter}`,
                onDelete: () => setLocationTypeFilter(""),
            });
        }

        if (eventNameFilter.trim()) {
            chips.push({
                key: `ev:${eventNameFilter}`,
                label: `행사명: ${eventNameFilter.trim()}`,
                onDelete: () => setEventNameFilter(""),
            });
        }

        if (locationFilter.trim()) {
            chips.push({
                key: `loc:${locationFilter}`,
                label: `장소명: ${locationFilter.trim()}`,
                onDelete: () => setLocationFilter(""),
            });
        }

        (activeThemes || []).forEach((t) => {
            chips.push({
                key: `th:${t}`,
                label: `테마: ${t}`,
                onDelete: () =>
                    setActiveThemes((prev) =>
                        (prev || []).filter(
                            (x) => normalizeKeyword(x) !== normalizeKeyword(t)
                        )
                    ),
            });
        });

        (activeKeywords || []).forEach((k) => {
            chips.push({
                key: `kw:${k}`,
                label: `키워드: ${k}`,
                onDelete: () =>
                    setActiveKeywords((prev) =>
                        (prev || []).filter(
                            (x) => normalizeKeyword(x) !== normalizeKeyword(k)
                        )
                    ),
            });
        });

        return chips;
    }, [
        activeKeywords,
        activeThemes,
        eventNameFilter,
        locationFilter,
        locationTypeFilter,
        normalizeKeyword,
    ]);

    const chipBase = ds.components.external.chip;
    const chipActive = ds.components.external.chipActive;
    const chipThemeActive = ds.components.external.chipSuccess;

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            {/* 페이지 헤더 */}
            <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
                spacing={1.5}
                mb={3}
            >
                <Box>
                    <Typography sx={ds.typography.pageTitle}>
                        3D 목록
                    </Typography>
                    <Typography sx={{ ...ds.typography.pageSubtitle, mt: 0.4 }}>
                        장소명 · 테마 · 키워드 AND 연계 필터
                    </Typography>
                </Box>

                <Stack direction="row" spacing={1}>
                    <ActionButton
                        variant="refresh"
                        onClick={refreshThemeAndKeywordOptions}
                        loading={filtersRefreshing}
                        disabled={loading}
                        label="필터 새로고침"
                    />

                    <ActionButton
                        variant="secondary"
                        startIcon={<RestartAltRoundedIcon sx={{ fontSize: 16 }} />}
                        onClick={resetFilters}
                        disabled={loading}
                        label="필터 초기화"
                    />
                </Stack>
            </Stack>

            {/* 필터 카드 */}
            <Paper sx={{ ...ds.components.external.card, p: 2.5, mb: 3 }}>
                {activeFilterChips.length > 0 && (
                    <>
                        <Typography
                            sx={{ ...ds.typography.caption, fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase", mb: 1 }}
                        >
                            적용 중인 필터
                        </Typography>

                        <Stack
                            direction="row"
                            spacing={0.75}
                            useFlexGap
                            flexWrap="wrap"
                            mb={2}
                        >
                            {activeFilterChips.map((c) => (
                                <Chip
                                    key={c.key}
                                    label={c.label}
                                    onDelete={c.onDelete}
                                    size="small"
                                    sx={{ ...chipActive, height: 26 }}
                                />
                            ))}
                        </Stack>

                        <Divider sx={{ mb: 2 }} />
                    </>
                )}

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
                        gap: 2,
                    }}
                >
                    <Box>
                        <Typography
                            sx={{ ...ds.typography.label, mb: 0.75 }}
                        >
                            실내/실외
                        </Typography>

                        <ComboBox clearable={false}
                            options={[
                                { value: "", label: "전체" },
                                { value: "실내", label: "실내" },
                                { value: "실외", label: "실외" },
                            ]}
                            value={locationTypeFilter}
                            onChange={(v) => setLocationTypeFilter(v)} />
                    </Box>

                    <Box>
                        <Typography
                            sx={{ ...ds.typography.label, mb: 0.75 }}
                        >
                            행사명
                        </Typography>

                        <TextField
                            value={eventNameFilter}
                            onChange={(e) => setEventNameFilter(e.target.value)}
                            placeholder="행사명 입력"
                            size="small"
                            fullWidth
                            InputProps={{
                                startAdornment: (
                                    <EventRoundedIcon
                                        sx={{ mr: 1, fontSize: 18, color: ds.colors.textMuted }}
                                    />
                                ),
                            }}
                        />
                    </Box>

                    <Box>
                        <Typography
                            sx={{ ...ds.typography.label, mb: 0.75 }}
                        >
                            장소명
                        </Typography>

                        <TextField
                            value={locationFilter}
                            onChange={(e) => setLocationFilter(e.target.value)}
                            placeholder="장소명 입력 (예: 코엑스)"
                            size="small"
                            fullWidth
                            InputProps={{
                                startAdornment: (
                                    <PlaceRoundedIcon
                                        sx={{ mr: 1, fontSize: 18, color: ds.colors.textMuted }}
                                    />
                                ),
                            }}
                        />
                    </Box>
                </Box>

                <Divider sx={{ my: 2.5 }} />

                <Box mb={2.5}>
                    <Stack direction="row" alignItems="center" spacing={1} mb={1.25}>
                        <Typography sx={ds.typography.sectionTitle}>
                            행사 성격 (테마)
                        </Typography>
                        <Typography sx={ds.typography.caption}>
                            클릭해서 선택/해제
                        </Typography>
                    </Stack>

                    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                        {allThemes.map((t, i) => {
                            const nt = normalizeKeyword(t);
                            const selected = activeThemeSet.has(nt);

                            return (
                                <Chip
                                    key={`${nt}-${i}`}
                                    label={nt}
                                    clickable
                                    onClick={() => toggleNormalized(nt, activeThemes, setActiveThemes)}
                                    sx={selected ? chipThemeActive : chipBase}
                                />
                            );
                        })}
                    </Stack>
                </Box>

                <Divider sx={{ mb: 2.5 }} />

                <Box>
                    <Stack direction="row" alignItems="center" spacing={1} mb={1.25}>
                        <Typography sx={ds.typography.sectionTitle}>
                            키워드
                        </Typography>
                        <Typography sx={ds.typography.caption}>
                            클릭해서 선택/해제
                        </Typography>
                    </Stack>

                    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" mb={2}>
                        {allKeywords.map((k, i) => {
                            const nk = normalizeKeyword(k);
                            const selected = activeKeywordSet.has(nk);

                            return (
                                <Chip
                                    key={`${nk}-${i}`}
                                    label={nk}
                                    clickable
                                    onClick={() =>
                                        toggleNormalized(nk, activeKeywords, setActiveKeywords)
                                    }
                                    sx={selected ? chipActive : chipBase}
                                />
                            );
                        })}
                    </Stack>

                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ pt: 1.5, borderTop: `1px solid ${ds.colors.borderLight}` }}
                    >
                        <Typography sx={{ fontSize: 13, color: ds.colors.textSecondary }}>
                            결과{" "}
                            <Box component="span" sx={{ fontWeight: 700, color: ds.colors.textPrimary }}>
                                {filtered.length.toLocaleString("ko-KR")}
                            </Box>
                            건 / 전체 {data.length.toLocaleString("ko-KR")}건
                        </Typography>

                        <ActionButton
                            variant="primary"
                            onClick={resetFilters}
                            disabled={loading}
                            label="초기화"
                        />
                    </Stack>
                </Box>
            </Paper>

            {/* 콘텐츠 카드 그리드 */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, 1fr)",
                        lg: "repeat(3, 1fr)",
                    },
                    gap: 2,
                }}
            >
                {filtered.map((d) => {
                    const thumb = d?.thumbnailFilePath
                        ? safeImgUrl(`${API_REAL}/${d.thumbnailFilePath}`)
                        : null;

                    return (
                        <Paper
                            key={d.id}
                            onClick={() => setDetailView(d)}
                            sx={ds.components.external.contentCard}
                        >
                            <Box
                                sx={{ ...ds.components.external.thumbnail, height: 220 }}
                            >
                                {thumb ? (
                                    <Box
                                        component="img"
                                        src={thumb}
                                        alt="썸네일"
                                        sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                                    />
                                ) : (
                                    <Stack alignItems="center" justifyContent="center" sx={{ height: "100%" }}>
                                        <Typography sx={{ fontSize: 13, color: ds.colors.textMuted }}>
                                            썸네일 없음
                                        </Typography>
                                    </Stack>
                                )}
                            </Box>

                            <Box sx={{ p: 2 }}>
                                <Stack spacing={1.25}>
                                    <Box minWidth={0}>
                                        <Typography
                                            sx={{
                                                fontSize: 15,
                                                fontWeight: 700,
                                                letterSpacing: "-0.2px",
                                                color: ds.colors.textPrimary,
                                            }}
                                            noWrap
                                        >
                                            {d?.eventName || "-"}
                                        </Typography>

                                        <Typography sx={{ fontSize: 12, color: ds.colors.textMuted, mt: 0.2 }} noWrap>
                                            {d?.locationType || "구분 없음"} · {d?.location || "장소 없음"}
                                        </Typography>
                                    </Box>

                                    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                                        {(d?.themes || []).slice(0, 3).map((t, idx) => (
                                            <Chip
                                                key={`t-${d.id}-${idx}`}
                                                label={normalizeKeyword(t)}
                                                size="small"
                                                sx={{
                                                    ...chipThemeActive,
                                                    height: 22,
                                                    "& .MuiChip-label": {
                                                        fontSize: 11,
                                                        fontWeight: 600,
                                                        color: "#166534",
                                                        px: 0.75,
                                                    },
                                                }}
                                            />
                                        ))}

                                        {(d?.keywords || []).slice(0, 3).map((k, idx) => (
                                            <Chip
                                                key={`k-${d.id}-${idx}`}
                                                label={normalizeKeyword(k)}
                                                size="small"
                                                sx={{
                                                    ...chipActive,
                                                    height: 22,
                                                    "& .MuiChip-label": {
                                                        fontSize: 11,
                                                        fontWeight: 600,
                                                        color: ds.colors.primary,
                                                        px: 0.75,
                                                    },
                                                }}
                                            />
                                        ))}
                                    </Stack>

                                    {/* 등록 일시 — BaseTimeEntity.createdAt 표시.
                                        수정/삭제/다운로드 액션은 카드 클릭 시 열리는 DetailModal 안으로 이동 (2D 패턴과 통일). */}
                                    {d?.createdAt && (
                                        <Typography sx={{ fontSize: 11, color: ds.colors.textMuted }}>
                                            등록 {String(d.createdAt).slice(0, 16).replace("T", " ")}
                                        </Typography>
                                    )}
                                </Stack>
                            </Box>
                        </Paper>
                    );
                })}
            </Box>

            {/* 무한 스크롤 sentinel */}
            {hasMore && (
                <Box ref={sentinelRef} sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                    {loading ? (
                        <LoadingDots compact label="추가 불러오는 중" />
                    ) : (
                        <Box sx={{ height: 20 }} />
                    )}
                </Box>
            )}

            {/* 첫 로딩 — 목록 영역 안에 inline LoadingDots (헤더/필터/칩은 즉시 보임). */}
            {loading && filtered.length === 0 && (
                <Box sx={{ py: 6 }}>
                    <LoadingDots />
                </Box>
            )}

            {!loading && filtered.length === 0 && (
                <Box sx={{ textAlign: "center", py: 8, color: ds.colors.textMuted }}>
                    <Typography sx={{ fontSize: 15, fontWeight: 600, mb: 0.5 }}>
                        결과가 없습니다
                    </Typography>
                    <Typography sx={{ fontSize: 13 }}>
                        필터를 변경하거나 초기화해 보세요
                    </Typography>
                </Box>
            )}

            {detailView && (
                <SpaceContentDetailModal
                    item={detailView}
                    onClose={() => setDetailView(null)}
                    onEdit={() => {
                        setEditingContent(detailView);
                        setDetailView(null);
                    }}
                    onDelete={async () => {
                        // handleDelete 가 confirm + API 호출. 성공 시 setData 에서 row 제거됨.
                        // 모달은 항상 닫기 (취소되어도 사용자가 명시적으로 X 로 닫는 것보다 깔끔).
                        await handleDelete(detailView.id);
                        setDetailView(null);
                    }}
                    onDownload={() => handleDownload(detailView)}
                    downloading={downloadingKey === downloadKeyOf(detailView)}
                />
            )}

            {editingContent && (
                <SpaceContentEditModal
                    item={editingContent}
                    onClose={() => setEditingContent(null)}
                    onSave={handleUpdate}
                />
            )}

            {/*
              다운로드 모달 — 자동 일괄 다운로드 대신 사용자가 항목별 버튼으로 받음.
              브라우저 차단(여러 파일 동시) / "10/10 완료"가 다 받은 뒤에야 뜨던 문제 해소.
            */}
            <Dialog
                open={!!downloadModal}
                onClose={() => {
                    if (downloadingAll) return; // 전체 다운로드 진행 중엔 닫기 금지 (진행 끊김 방지)
                    setDownloadModal(null);
                }}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ pr: 6, fontWeight: 700 }}>
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            파일 다운로드
                            <Typography sx={{ fontSize: 13, color: ds.colors.textMuted, mt: 0.5 }}>
                                {downloadModal?.event} · {downloadModal?.location}
                                {downloadModal && (
                                    <> · 총 {downloadModal.files.length}개 (완료 {downloadDone.size}개)</>
                                )}
                            </Typography>
                        </Box>
                        {downloadModal && downloadModal.files.length > 0 && (
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<DownloadRoundedIcon />}
                                onClick={handleDownloadAll}
                                disabled={
                                    downloadingAll ||
                                    downloadDone.size === downloadModal.files.length
                                }
                                sx={{ whiteSpace: "nowrap", mr: 4 }}
                            >
                                {downloadingAll
                                    ? `다운로드 중... (${downloadDone.size}/${downloadModal.files.length})`
                                    : downloadDone.size === downloadModal.files.length
                                        ? "전체 완료"
                                        : "전체 다운로드"}
                            </Button>
                        )}
                    </Box>
                    <IconButton
                        onClick={() => setDownloadModal(null)}
                        disabled={downloadingAll}
                        sx={{ position: "absolute", right: 12, top: 12 }}
                    >
                        <CloseRoundedIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0 }}>
                    {downloadModal?.files?.map((f, i) => {
                        const ext = (f.filename || "").split(".").pop() || "bin";
                        const displayName = `${downloadModal.safeBase}_${i + 1}.${ext}`;
                        const isDone = downloadDone.has(i);
                        const isBusy = downloadBusyIndex === i;
                        return (
                            <Box
                                key={i}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                    px: 2.5,
                                    py: 1.5,
                                    borderBottom: `1px solid ${ds.colors.borderLight}`,
                                    "&:last-of-type": { borderBottom: "none" },
                                    bgcolor: isDone ? "rgba(46, 174, 96, 0.06)" : "transparent",
                                }}
                            >
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        sx={{
                                            fontSize: 14,
                                            fontWeight: 600,
                                            color: ds.colors.text,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {displayName}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: 12,
                                            color: ds.colors.textMuted,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        원본: {f.filename}
                                    </Typography>
                                </Box>
                                {isDone ? (
                                    <Button
                                        variant="text"
                                        size="small"
                                        startIcon={<CheckCircleRoundedIcon />}
                                        onClick={() => handleDownloadOne(i)}
                                        disabled={downloadingAll}
                                        sx={{ color: "#2eae60", whiteSpace: "nowrap" }}
                                    >
                                        다시 받기
                                    </Button>
                                ) : (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<DownloadRoundedIcon />}
                                        onClick={() => handleDownloadOne(i)}
                                        disabled={isBusy || downloadingAll}
                                        sx={{ whiteSpace: "nowrap" }}
                                    >
                                        {isBusy ? "받는 중..." : "다운로드"}
                                    </Button>
                                )}
                            </Box>
                        );
                    })}
                    {downloadModal?.files?.length === 0 && (
                        <Box sx={{ textAlign: "center", py: 5, color: ds.colors.textMuted }}>
                            <Typography sx={{ fontSize: 13 }}>다운로드할 파일이 없습니다.</Typography>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            {/*
              풀스크린 Backdrop 제거 — 첫 로드는 위 inline LoadingDots, 무한 스크롤 추가 로드는 sentinel.
              filtersRefreshing 은 "필터 새로고침" 버튼의 라벨이 "새로고침 중..." 으로 바뀌어 이미 안내됨.
            */}

        </Container>
    );
}
